import { Injectable, OnDestroy } from "@angular/core";
import * as signalR from "@microsoft/signalr";
import { Subject, take } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class TripSignalrService implements OnDestroy {
  private hubConnection: signalR.HubConnection;

  // Add subjects for notifying components about SignalR events
  public userJoined = new Subject<{ tripId: string; username: string }>();
  public userLeft = new Subject<{ tripId: string; username: string }>();
  public activeUsers = new Subject<{ tripId: string; users: string[] }>();
  public userViewing = new Subject<{ tripId: string; username: string }>();
  public allActiveTrips = new Subject<{ [tripId: string]: string[] }>();
  public connectionEstablished = new Subject<void>();

  private heartbeatInterval: any;
  private currentUsername: string | null = null;
  private currentTripId: string | null = null;

  constructor() {
    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl("/triphub")
      .withAutomaticReconnect()
      .build();

    this.hubConnection.on("UserJoined", (tripId, username) => {
      console.log(`User ${username} joined trip ${tripId}`);
      this.userJoined.next({ tripId, username });
    });

    this.hubConnection.on("UserLeft", (tripId, username) => {
      console.log(`User ${username} left trip ${tripId}`);
      this.userLeft.next({ tripId, username });
    });

    this.hubConnection.on("ActiveUsers", (tripId, users) => {
      console.log(`Active users for trip ${tripId}:`, users);
      this.activeUsers.next({ tripId, users });
    });

    this.hubConnection.on("UserViewing", (tripId, username) => {
      console.log(`User ${username} is viewing trip ${tripId}`);
      this.userViewing.next({ tripId, username });
    });

    this.hubConnection.on("AllActiveTrips", (allTrips: { [tripId: string]: string[] }) => {
      console.log("All active trips:", allTrips);
      this.allActiveTrips.next(allTrips);
    });

    document.addEventListener("visibilitychange", this.handleVisibilityChange.bind(this));

    this.start();
  }

  private async start(): Promise<void> {
    try {
      await this.hubConnection.start();
      console.log("SignalR Connected");
      this.connectionEstablished.next();
    } catch (err) {
      console.error("SignalR Connection Error: ", err);
      setTimeout(() => this.start(), 5000);
    }
  }

  public ensureConnection(): Promise<void> {
    if (this.hubConnection && this.hubConnection.state === signalR.HubConnectionState.Connected) {
      return Promise.resolve();
    } else if (
      this.hubConnection &&
      this.hubConnection.state === signalR.HubConnectionState.Connecting
    ) {
      // Return a promise that resolves when the connection is established
      return new Promise<void>((resolve) => {
        this.connectionEstablished.pipe(take(1)).subscribe(() => resolve());
      });
    } else {
      // Start the connection
      return this.start();
    }
  }

  public getConnectionState(): signalR.HubConnectionState {
    return this.hubConnection.state;
  }

  // Method to join a trip with heartbeat
  public joinTrip(tripId: string, username: string): void {
    this.currentTripId = tripId;
    this.currentUsername = username;

    // Clear any existing heartbeat interval
    this.clearHeartbeat();

    // Join the trip
    this.hubConnection
      .invoke("JoinTrip", tripId, username)
      .catch((err) => console.error("Error joining trip:", err));

    // Start the heartbeat
    this.startHeartbeat();
  }

  // Method to leave a trip
  public leaveTrip(): void {
    if (this.currentTripId && this.currentUsername) {
      this.hubConnection
        .invoke("LeaveTrip", this.currentTripId, this.currentUsername)
        .catch((err) => console.error("Error leaving trip:", err));

      this.clearHeartbeat();
      this.currentTripId = null;
      this.currentUsername = null;
    }
  }

  // Start sending regular heartbeats
  private startHeartbeat(): void {
    // Send heartbeats every 30 seconds
    this.heartbeatInterval = setInterval(() => {
      if (
        this.currentTripId &&
        this.currentUsername &&
        this.hubConnection.state === signalR.HubConnectionState.Connected
      ) {
        console.log("Sending heartbeat for trip:", this.currentTripId);
        this.hubConnection
          .invoke("Heartbeat", this.currentTripId, this.currentUsername)
          .catch((err) => console.error("Error sending heartbeat:", err));
      }
    }, 30000); // 30 seconds
  }

  // Clear the heartbeat interval
  private clearHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  public subscribeToAllTrips(): Promise<void> {
    return this.ensureConnection().then(() => {
      this.hubConnection
        .invoke("SubscribeToAllTrips")
        .catch((err) => console.error("Error subscribing to all trips:", err));
    });
  }

  public unsubscribeFromAllTrips(): void {
    if (this.hubConnection && this.hubConnection.state === signalR.HubConnectionState.Connected) {
      this.hubConnection
        .invoke("UnsubscribeFromAllTrips")
        .catch((err) => console.error("Error unsubscribing from all trips:", err));
    }
  }

  private handleVisibilityChange(): void {
    if (document.visibilityState === "visible") {
      console.log("Tab became active, checking heartbeat and connection");

      // Check connection status and reconnect if necessary
      this.ensureConnection().then(() => {
        if (this.currentTripId && this.currentUsername) {
          // Restart the heartbeat
          this.clearHeartbeat();
          this.startHeartbeat();

          // send an immediate heartbeat
          if (this.hubConnection.state === signalR.HubConnectionState.Connected) {
            this.hubConnection
              .invoke("Heartbeat", this.currentTripId, this.currentUsername)
              .catch((err) => console.error("Error sending heartbeat on visibility change:", err));
          }
        }
      });
    }
  }

  // Handle disconnection properly
  ngOnDestroy(): void {
    document.removeEventListener("visibilitychange", this.handleVisibilityChange.bind(this));

    this.leaveTrip();
    this.unsubscribeFromAllTrips();
    this.clearHeartbeat();
    if (this.hubConnection) {
      this.hubConnection.stop();
    }
  }
}
